import {
  PromptRun,
  PromptRunFailedSegment,
  PromptRunFailedSegmentsManifest,
  PromptRunFailureOperationCounts,
} from '../services/api';

export const PROMPT_RUN_TERMINAL_STATUSES = new Set([
  'completed',
  'failed',
  'completed_with_failures',
  'cancelled',
]);

export interface OperationDisplayState {
  state: 'ok' | 'pending' | 'failed' | 'disabled';
  text: string;
}

const safeCount = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  return Math.max(0, Math.floor(parsed));
};

export const isPromptRunTerminalStatus = (status?: string | null): boolean => {
  if (!status) return false;
  return PROMPT_RUN_TERMINAL_STATUSES.has(String(status).toLowerCase());
};

export const getOperationDisplayState = (
  enabled: boolean,
  succeeded: boolean | null | undefined,
  failureCount: number,
  runStatus?: string | null
): OperationDisplayState => {
  const normalizedFailures = safeCount(failureCount);
  if (!enabled) {
    return { state: 'disabled', text: 'disabled' };
  }
  if (normalizedFailures > 0) {
    return { state: 'failed', text: `${normalizedFailures} failed` };
  }
  if (succeeded === true) {
    return { state: 'ok', text: 'ok' };
  }
  if (succeeded === false) {
    return { state: 'failed', text: 'failed' };
  }
  return { state: 'pending', text: isPromptRunTerminalStatus(runStatus) ? 'no result' : 'pending' };
};

export const getOperationDetailLabel = (state: OperationDisplayState): string => (
  state.state === 'ok' ? 'Succeeded' : state.text.charAt(0).toUpperCase() + state.text.slice(1)
);

export const emptyOperationCounts = (): PromptRunFailureOperationCounts => ({
  field_extraction: 0,
  image_embedding: 0,
  processing: 0,
});

export const getRunFailureCounts = (
  run: PromptRun,
  manifest?: PromptRunFailedSegmentsManifest | null
): PromptRunFailureOperationCounts => {
  if (manifest?.operation_counts) {
    return {
      field_extraction: Math.max(safeCount(run.field_extraction_failures), safeCount(manifest.operation_counts.field_extraction)),
      image_embedding: safeCount(manifest.operation_counts.image_embedding),
      processing: safeCount(manifest.operation_counts.processing),
    };
  }
  return {
    field_extraction: safeCount(run.field_extraction_failures),
    image_embedding: 0,
    processing: 0,
  };
};

export const getManifestVideoFailureMap = (
  manifest?: PromptRunFailedSegmentsManifest | null
): Record<string, { operation_counts: PromptRunFailureOperationCounts; segments: PromptRunFailedSegment[]; failed_segments: number }> => {
  if (!manifest?.videos?.length) {
    return {};
  }
  return manifest.videos.reduce<Record<string, { operation_counts: PromptRunFailureOperationCounts; segments: PromptRunFailedSegment[]; failed_segments: number }>>((acc, video) => {
    acc[video.video_id] = {
      operation_counts: video.operation_counts || emptyOperationCounts(),
      segments: Array.isArray(video.segments) ? video.segments : [],
      failed_segments: safeCount(video.failed_segments),
    };
    return acc;
  }, {});
};
